import { Component, OnInit, ViewChild, AfterViewInit, Optional } from '@angular/core';
import { Subscription } from "rxjs";
import { tap, finalize } from "rxjs/operators";
import esri = __esri;
import { BaseMapControlComponent } from "../base-map-control/base-map-control.component";
import { AccessPlan } from "../../../gis/models/access-plan.model";
import { formattedRouteString } from "../../../gis/models/formatted-route-string.model";
import { OdnrMapComponent } from "../../../gis/components/odnr-map/odnr-map.component";
import { GisLocationService, CoordinateSystem } from "../../../gis/services/gis-location.service";
import { WellConfigurationIndex, ensureWellComponentConfig, getContextLocation, WellConfigType, StatePlane, WellDirection } from "../new-casing-plan/models/casing-plan.model";
import { RendererTypes } from "../../../builder/components/state/controls/config/data/well-related/map-control-data-config.component";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { Renderer } from "../../../gis/components/odnr-map/renderer/renderer.interface";
import { DocumentManagementService } from "../../../common/services/document-management.service";
import { RouteMapRenderer2D } from "../../../gis/components/odnr-map/renderer/routeMapRenderer";
import { ContextInteractionService } from '../../services/context-interaction.service';

@Component({
    selector: 'route-control',
    templateUrl: './route-control.component.html',
    standalone: false
})
export class RouteControlComponent extends BaseMapControlComponent implements OnInit, AfterViewInit {
    static componentName = "RouteControlComponent";

    defaultZoom: number = 15;
    dataSubscription = new Subscription();

    ingressEgress: string = "ingress";

    routeStrings: formattedRouteString[] = [];

    get wellDirection(): string {
        if (this.isEdit) return "";
        return this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.WellDirection));
    }
    @ViewChild(OdnrMapComponent, { static: true }) odnrMapComponent: OdnrMapComponent;

    constructor(
        public readonly gisLocationService: GisLocationService,
        @Optional() public readonly buttonOverride: FlowButtonOverrideService,
        @Optional() protected readonly _docService: DocumentManagementService,
        public readonly contextService: ContextInteractionService
    ) {
        super(buttonOverride, _docService, contextService);
    }

    ngOnInit() {
        if (!this.componentData.label || this.componentData.label.length === 0) {
            this.componentData.label = "<p>Click on the map to add stops to the route.<br />Click on an existing point to remove it from the route.</p>";
        }
        super.ngOnInit();
        this.componentData.config.wellDataPoints = ensureWellComponentConfig(WellConfigType.Map, this.componentData.config.wellDataPoints);
        // Get Existing Values
        
        this.storedLatitude = Number(this.getContext("latitude"));
        this.storedLongitude = Number(this.getContext("longitude"));

        if (this.componentData.config.contextLocations == null) {
            const x = this.getNAD83LongitudeFromContext();
            const y = this.getNAD83LatitudeFromContext();
            const plane = this.getStatePlane().toLowerCase() === StatePlane.South
                ? CoordinateSystem.OHIO_SOUTH
                : CoordinateSystem.OHIO_NORTH;

            const latLong = this.gisLocationService.statePlaneToWebMercator(x, y, plane, true);
            if (latLong.length === 2) {
                this.currentLatitude = latLong[0];
                this.currentLongitude = latLong[1];
                this.storeContext(this.currentLatitude, "latitude");
                this.storeContext(this.currentLongitude, "longitude");
            }
        } else {
            this.currentLatitude = this.getLatitudeFromContext();
            this.currentLongitude = this.getLongitudeFromContext();
            this.storeContext(this.currentLatitude, "latitude");
            this.storeContext(this.currentLongitude, "longitude");
        }        

        this.componentData.config.properties = { zoom: this.defaultZoom, center: [this.currentLongitude, this.currentLatitude] };
        this.componentData.config.rendererType = RendererTypes.Route;
        let context = this.getContextValue();

        if (!context || (!context.ingress && !context.path))
            return;
        let points = context.points;
        let formattedPoints: formattedRouteString[] = [];
        if (context.ingress) {
            let ingressPoints: any[] = context.ingress;
            ingressPoints.forEach(x => formattedPoints.push(new formattedRouteString(x.streetName, x.crossStreet)));
        } else if (context.path) {
            formattedPoints = this.parsePathToFormattedPoints(context.path);
        }
        this.storedAccessPlan = new AccessPlan(formattedPoints, true, points);
        this.currentAccessPlan = new AccessPlan(formattedPoints, true, points);
        this.routeStrings = this.currentAccessPlan.ingress;
    }

    ngAfterViewInit() {
        
    }

    ingressEgressChanged(): void {
        (this.mapRenderer as RouteMapRenderer2D).ingressMode = (this.ingressEgress === "ingress");
        this.routeStrings = this.routeStrings.reverse();
    }

    onMapReady(data: Renderer<esri.SceneView | esri.MapView>): void {
        super.onMapReady(data);        
        let data$ = this.mapRenderer.dataUpdated.pipe(
            tap((data) => {        
                if (this.isEnabled) this.updateRoutes(data as AccessPlan);              
            }),
            finalize(() => { this.dataSubscription.unsubscribe(); })
        ).subscribe(
            (data) => { console.log("routes updated"); },
            (error) => { console.error(error); },
            () => { this.dataSubscription.unsubscribe(); }
        );
        this.dataSubscription.add(data$);
    }

    updateRoutes(routes: AccessPlan): void {
        if (!this.componentData.contextTarget) return;
        this.setContextValue(routes);
        this.currentAccessPlan = routes;
        this.routeStrings = (this.ingressEgress === "ingress") ? this.currentAccessPlan.ingress : this.currentAccessPlan.egress;
    }

    private getLatitudeFromContext(): number {
        const location = this.componentData.config.contextLocations.latitude;
        const value = this.getContextData(location);
        return Number(value);
    }

    private getLongitudeFromContext(): number {
        const location = this.componentData.config.contextLocations.longitude;
        const value = this.getContextData(location);
        return Number(value);
    }

    private getNAD83LatitudeFromContext(): number {
        if (this.isEdit) return 0;
        const direction = this.wellDirection || "";
        switch (direction) {
            case WellDirection.Directional:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeDirectional) || ""));
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeHorizontal) || ""));
            case WellDirection.Vertical:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LatitudeVertical) || ""));
            default:
                return 0;
        }
    }

    private getNAD83LongitudeFromContext(): number {
        if (this.isEdit) return 0;
        const direction = this.wellDirection || "";
        switch (direction) {
            case WellDirection.Directional:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeDirectional) || ""));
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeHorizontal) || ""));
            case WellDirection.Vertical:
                return Number(this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.LongitudeVertical) || ""));
            default:
                return 0;
        }
    }

    private getStatePlane(): string {
        if (this.isEdit) return StatePlane.South;
        const direction = this.wellDirection || "";
        let plane = StatePlane.South;
        switch (direction) {
            case WellDirection.Directional:
                plane = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneDirectional));
                break;
            case WellDirection.OtherHorizontal:
            case WellDirection.Horizontal:
                plane = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneHorizontal));
                break;
            case WellDirection.Vertical:
                plane = this.getContextData(getContextLocation(this.componentData.config.wellDataPoints, WellConfigurationIndex.StatePlaneVertical));
                break;
            default:
                break;
        }
        return plane;
    }

    private parsePathToFormattedPoints(path: string): formattedRouteString[] {
        let formattedRoutes: formattedRouteString[] = [];
        let pathParts = path.split(',');
        pathParts.forEach(p => {
            if (p.indexOf('/') !== -1) {
                let pieces = p.split('/');
                formattedRoutes.push(new formattedRouteString(pieces[0], pieces[1]));
            } else {
                formattedRoutes.push(new formattedRouteString(p, null));
            }
        });
        return formattedRoutes;
    }

}
